import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import { useEffect, useRef, useState } from "react";

import { useSetProjectPropertyStyle } from "../../api/projects";
import { type BoundaryStyle, DEFAULT_PROPERTY_BOUNDARY_STYLE, resolveBoundaryStyle } from "../../types/boundaryStyle";
import type { Project } from "../../types/project";
import BoundaryStyleEditor from "./BoundaryStyleEditor";

interface PropertyStyleDialogProps {
  open: boolean;
  /** The project, as the list currently has it. */
  project: Project | null;
  onClose: () => void;
}

/** Sliders move faster than the server needs to hear about. */
const SAVE_DELAY_MS = 250;

/**
 * How one project's property boundaries are drawn. Changes preview on the
 * listing map straight away (the mutation is optimistic on the projects list)
 * and are saved a moment after the last change, for everyone.
 */
export default function PropertyStyleDialog({ open, project, onClose }: PropertyStyleDialogProps) {
  const setStyle = useSetProjectPropertyStyle();
  const [style, setLocal] = useState<BoundaryStyle>(DEFAULT_PROPERTY_BOUNDARY_STYLE);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    if (open) setLocal(resolveBoundaryStyle(project?.property_style, DEFAULT_PROPERTY_BOUNDARY_STYLE));
    // Only when it opens, or for another project: the list's copy changes
    // under us as our own saves land.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, project?.id]);
  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    []
  );

  const save = (next: BoundaryStyle | null, delay: number) => {
    if (!project) return;
    const projectId = String(project.id);
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      timer.current = null;
      setStyle.mutate({ projectId, style: next });
    }, delay);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <PaletteOutlinedIcon sx={{ color: "#388e3c" }} />
        {project ? `Property boundaries of “${project.name}”` : "Property boundary style"}
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
        <Typography variant="body2" color="text.secondary">
          How this project&apos;s property boundaries are drawn on the listing map and the project map,
          for everyone. Changes show on the map as you make them.
        </Typography>
        <BoundaryStyleEditor
          value={style}
          defaults={DEFAULT_PROPERTY_BOUNDARY_STYLE}
          onChange={(next) => {
            setLocal(next);
            save(next, SAVE_DELAY_MS);
          }}
          onReset={() => {
            setLocal(DEFAULT_PROPERTY_BOUNDARY_STYLE);
            save(null, 0);
          }}
        />
        {setStyle.isError && (
          <Typography variant="caption" color="error">
            {setStyle.error?.message || "Couldn't save the style. Please try again."}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  );
}
